import { createContext, useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { getCookie } from "common/cookies";
import { InitialLocationContext } from "custom-types/FinderPage";
import { Location } from "custom-types/Location";
import { getDomainCountryCode } from "redux/selectors/config";
import {
  getLocationSlug,
  normalizeFinderLocation,
  setLocationCookie,
} from "utils/locationUtils";
import { clearAllQueryParams } from "utils/nextRouterUtils";

import MapContext from "./MapContext";

const noop = () => {};

export type LocationContextProps = {
  setLocation: (location: Location) => void;
  setHasSelectedLocation: (hasSelectedLocation: boolean) => void;
  finderLocation: Location;
  locationParam: string | null;
  userLocation: Location;
  userHasSelectedLocation: boolean;
};

export const locationContext = {
  finderLocation: {} as Location,
  locationParam: null,
  setHasSelectedLocation: noop,
  setLocation: noop,
  userHasSelectedLocation: false,
  userLocation: {} as Location,
};

const LocationContext = createContext<LocationContextProps>(locationContext);

LocationContext.displayName = "FinderLocationContext";

export const LocationProvider = ({
  data,
  children,
}: {
  data: InitialLocationContext;
  children: React.ReactNode;
}) => {
  const [userLocation, setUserLocation] = useState(data.userLocation || {});

  const { setBoundingBox } = useContext(MapContext);

  const [finderLocation, setFinderLocation] = useState<Location>({
    ...data.finderLocation,
    geoSlug: getLocationSlug({
      city: data.finderLocation?.city,
      state: data.finderLocation?.state || "",
      stateCode: data.finderLocation?.stateCode,
    }),
  });
  const [locationParam, setLocationParam] = useState(data.locationParam);
  const [userHasSelectedLocation, setHasSelectedLocation] = useState(false);
  const countryCode = useSelector(getDomainCountryCode);
  // setters
  const handleUpdateLocation = (location: Location) => {
    clearAllQueryParams(false, ["filter"]);
    setBoundingBox();
    const normalizedLocation = normalizeFinderLocation(location);
    if (normalizedLocation && normalizedLocation !== finderLocation) {
      // location should be in cookie format -- add test to verify when working on DISCO-1684
      setLocationCookie({
        countryCode,
        isUserLocation: location?.isUserLocation ?? false, // geolocated location will have isUserLocation set to true
        location,
      });
      normalizedLocation && setFinderLocation(normalizedLocation);
    }
    if (location.geoSlug && location.geoSlug !== locationParam) {
      setLocationParam(location.geoSlug);
    }
  };

  const handleUpdateUserLocation = (location: Location) => {
    if (location !== userLocation) {
      setUserLocation(location);
    }
  };

  const handleLocationUpdate = (e: Event & { detail?: Location }) => {
    if (e.detail) {
      const normalizedLocation = normalizeFinderLocation(e.detail);
      handleUpdateLocation(e.detail);
      if (normalizedLocation) {
        handleUpdateUserLocation(normalizedLocation);
      }
      setHasSelectedLocation(true);
    }
  };

  useEffect(() => {
    const hasUserSetLocation =
      window && !!getCookie("leafly-location-selected");
    setHasSelectedLocation(hasUserSetLocation);
    window.addEventListener("userLocationUpdated", handleLocationUpdate);
    window.Leafly.disableLocationRefresh = true;
    return () => {
      window.removeEventListener("userLocationUpdated", handleLocationUpdate);
      window.Leafly.disableLocationRefresh = false;
    };
  }, [finderLocation]);

  return (
    <LocationContext.Provider
      value={{
        finderLocation,
        locationParam,
        setHasSelectedLocation,
        setLocation: handleUpdateLocation,
        userHasSelectedLocation,
        userLocation,
      }}
    >
      {children}
    </LocationContext.Provider>
  );
};

export default LocationContext;
